import { Injectable } from '@angular/core';
import { ApiService, DeliveryAddress, DeliveryFee, Order, OrderState, Table } from '@us/web/shared/data-access';
import { map, Observable } from 'rxjs';
import { isNil } from 'lodash';
import { getFormattedAddress, parseDataServer } from '@us/web/shared/utils';
import { data } from 'autoprefixer';

@Injectable({
    providedIn: 'root'
})
export class OrdersService {
    private readonly url = 'orders';
    private readonly pushOrderStateUrl = 'order/state';
    private readonly estimateOrderFeeUrl = 'order/estimated-fee';
    private readonly transactionStatusOrderUrl = 'order/transaction-status';
    private readonly callWaiterUrl = 'call-waiter';
    private readonly pushNotificationUrl = 'order/push-notification';

    constructor(private apiService: ApiService) {}

    getOrders(): Observable<Order[]> {
        return this.apiService.get(this.url).pipe(
            map(response => {
                return response.map((e: any) => parseDataServer(e));
            })
        );
    }

    getOrder(id: string, fields?: string[]): Observable<Order> {
        return this.apiService
            .get('order', { params: { id, t: Date.now(), fields: fields || [] } })
            .pipe(map(response => parseDataServer(response)));
    }

    pushOrderState(params: { order_id: string; state: OrderState; message?: string }): Observable<Order> {
        return this.apiService.put(this.pushOrderStateUrl, params).pipe(map(response => parseDataServer(response)));
    }

    getTransactionStatus(id: string) {
        return this.apiService
            .get(this.transactionStatusOrderUrl, { params: { id } })
            .pipe(map(response => parseDataServer(response)));
    }

    callWaiter(table: Table) {
        const params = {
            table_id: table.id,
            table_name: table.name,
            area_id: table.area_id,
            area_name: table.area_name
        };
        return this.apiService.post(this.callWaiterUrl, params);
    }

    postRating(params: { rating: number; description: string; order_id: string; account_id: number }) {
        return this.apiService.post('rating', params);
    }

    estimateOrderFee(receiver: DeliveryAddress, total: number, shopId?: string): Observable<DeliveryFee[]> {
        if (isNil(receiver)) {
            throw new Error('Invalid receiver');
        }

        const { latitude, longitude, city_id, district_id } = receiver;

        const address = getFormattedAddress(receiver);

        const data = {
            address,
            latitude,
            longitude,
            city_id,
            district_id,
            total,
            ...(shopId ? { shop_id: shopId } : {})
        };

        return this.apiService.post(this.estimateOrderFeeUrl, data);
    }

    validateTable(params: { tableId: number; shopId?: string }) {
        return this.apiService.post<Table>(`validate-table`, params);
    }
}
